'use client';

import { css } from '@repo/pandacss/styled-system/css';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { Roundup, SummaryTableRowType } from '@repo/supergraph/roundup/base/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { useGeo } from '../GeoProvider/hook';
import { getFilterSlugForPage } from '../roundups/OffersFilter/utils';
import ProductCardCategorySections from '../roundups/ProductCardCategorySections';
import { ProductCardCategorySectionsWithOffersStore } from '../roundups/ProductCardCategorySections/ProductCardCategorySectionsWithOffersStore';
import { SummaryTableSection } from '../SummaryTableSection/SummaryTableSection';
import { SummaryTableSectionWithOffersStore } from '../SummaryTableSection/SummaryTableSectionWithOffersStore';
import { ProductsSectionWrapper } from './Wrapper';

type PaidProductsSectionProps = {
  deviceType?: MobileOrDesktop;
  initialLocation?: StateAbbreviation;
  pageType: 'roundup' | 'paid-roundup';
  pageUrl: string;
  productCardsSection: {
    categorySections: Roundup['productCardsSection']['categorySections'];
  };
  rows: SummaryTableRowType[];
  shouldApplyStylingUpdates?: boolean;
  slug: string;
  suppressApyTermPrefix?: boolean;
};

const filtersEnabledSectionContainerStyles = css({
  marginTop: { base: '7', md: '0' },
});

export function PaidProductsSection({
  deviceType,
  initialLocation,
  pageType,
  pageUrl,
  productCardsSection,
  rows,
  shouldApplyStylingUpdates = false,
  slug,
  suppressApyTermPrefix = false,
}: PaidProductsSectionProps) {
  const filterSlug = getFilterSlugForPage(slug, true, Boolean(initialLocation));
  const isFilterEnabled = Boolean(filterSlug);
  const { regionCode } = useGeo();

  return (
    <section
      data-testid="products-container"
      className={isFilterEnabled ? filtersEnabledSectionContainerStyles : undefined}
    >
      <ProductsSectionWrapper
        isFilterActive={isFilterEnabled}
        pageSlug={slug}
        filterSlug={filterSlug}
        initialLocation={initialLocation}
        sortedRows={rows}
      >
        {isFilterEnabled ? (
          <SummaryTableSectionWithOffersStore
            deviceType={deviceType}
            pageType={pageType}
            rows={rows}
            pageUrl={pageUrl}
            regionCode={regionCode}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug}
            suppressApyTermPrefix={suppressApyTermPrefix}
          />
        ) : (
          <SummaryTableSection
            deviceType={deviceType}
            pageType={pageType}
            rows={rows}
            pageUrl={pageUrl}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug}
            suppressApyTermPrefix={suppressApyTermPrefix}
          />
        )}
        {isFilterEnabled ? (
          <ProductCardCategorySectionsWithOffersStore
            deviceType={deviceType}
            categorySections={productCardsSection.categorySections}
            pageType={pageType}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug}
            regionCode={regionCode}
          />
        ) : (
          <ProductCardCategorySections
            deviceType={deviceType}
            categorySections={productCardsSection.categorySections}
            pageType={pageType}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug}
          />
        )}
      </ProductsSectionWrapper>
    </section>
  );
}
